import dayjs from "dayjs";
import { AlertCircle, Calendar, Clock, MapPin } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EVENT_LOCATION_TYPE_ONLINE, LANG_JP } from "@/constants";
import { useEventLinkPreviewContext } from "@/contexts/EventLinkPreviewContext";
import { useDeferredEnabled } from "@/hooks/useDeferredEnabled";
import { useLinkPreview } from "@/hooks/useLinkPreview";
import { usePublicFileByGuid } from "@/hooks/usePublicFileByGuid";
import type { EventApiResponse } from "@/lib/humhub-api/types";
import { LoadingIndicator } from "./LoaderScreen";

interface InlineEventLinkPreviewProps {
  url: string;
  onEventClick?: (eventData: EventApiResponse) => void;
}

export const InlineEventLinkPreview: React.FC<InlineEventLinkPreviewProps> = ({
  url,
  onEventClick,
}) => {
  // All hooks MUST be called unconditionally
  const canFetchPreview = useDeferredEnabled(true, 1200);
  const { metadata, isLoading, error, errorStatus } = useLinkPreview(url, {
    enabled: canFetchPreview,
  });
  const { t, i18n } = useTranslation();
  const [hasImageError, setHasImageError] = useState(false);
  const {
    onEventClick: contextOnEventClick,
    currentStudyGroupId,
    showOriginalContent,
  } = useEventLinkPreviewContext();
  const effectiveOnEventClick = onEventClick ?? contextOnEventClick;

  // Extract data safely (can be undefined)
  const event = metadata?.eventData;

  // Get the first file's guid from event data
  const fileGuid = event?.content?.files?.[0]?.guid;

  // Fetch the image using the file guid
  const { fileBlobUrl, isLoading: isImageLoading } = usePublicFileByGuid(
    fileGuid,
    Boolean(event),
  );

  // Use the fetched image
  const previewImage = fileBlobUrl || undefined;

  // useEffect - called unconditionally
  useEffect(() => {
    if (previewImage) {
      setHasImageError(false);
    }
  }, [previewImage]);

  // useCallback - called unconditionally
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!event) return;

      const isInCurrentStudyGroup =
        currentStudyGroupId &&
        event.container?.id &&
        String(event.container.id) === String(currentStudyGroupId);

      if (isInCurrentStudyGroup && effectiveOnEventClick) {
        e.preventDefault();
        effectiveOnEventClick(event);
      }
    },
    [effectiveOnEventClick, currentStudyGroupId, event],
  );

  if (!canFetchPreview || isLoading) {
    return (
      <LoadingIndicator
        inline
        label={t("common.loading")}
        className="text-gray-500"
      />
    );
  }

  if (error || !event) {
    const previewErrorMessage =
      errorStatus === 404
        ? t("common.eventPreviewMissing")
        : t("common.eventPreviewUnavailable");

    return (
      <span className="inline-flex max-w-full flex-col gap-1">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="text-blue-600 hover:underline"
        >
          {url}
        </a>
        <span className="inline-flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900">
          <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
          <span className="min-w-0">
            <span>{previewErrorMessage}</span>
          </span>
        </span>
      </span>
    );
  }

  // Rest of logic after we know event exists
  const startDate = dayjs(event.start_datetime);
  const isOnline = event.location_type === EVENT_LOCATION_TYPE_ONLINE;
  const previewImageAlt =
    (showOriginalContent && event.title_original
      ? event.title_original
      : event.title) ||
    t("common.previewImage", { defaultValue: "Preview image" });
  const displayTitle =
    showOriginalContent && event.title_original
      ? event.title_original
      : event.title;

  const isJapanese = i18n.language === LANG_JP;
  const dateFormat = isJapanese ? "MM/DD HH:mm" : "MMM DD HH:mm";

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      onClick={handleClick}
      className="inline-flex max-w-full items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
      style={{ textDecoration: "none" }}
    >
      {previewImage && !hasImageError && !isImageLoading ? (
        <span className="inline-flex size-10 shrink-0 overflow-hidden rounded-md border border-blue-200 bg-white">
          <img
            src={previewImage}
            alt={previewImageAlt}
            className="size-full object-cover"
            loading="lazy"
            onError={() => setHasImageError(true)}
          />
        </span>
      ) : (
        <Calendar className="size-3.5 shrink-0" />
      )}
      <span className="inline-flex min-w-0 items-center gap-1 font-semibold">
        <span className="truncate text-sm leading-tight">{displayTitle}</span>
      </span>
      <span className="inline-flex items-center gap-1 text-xs text-blue-600">
        <Clock className="size-3 shrink-0" />
        <span>{startDate.format(dateFormat)}</span>
      </span>
      <span className="inline-flex items-center gap-1 text-xs text-blue-600">
        <MapPin className="size-3 shrink-0" />
        <span className="truncate">
          {isOnline ? t("common.online") : t("common.inPerson")}
        </span>
      </span>
    </a>
  );
};
